import {
  Button,
  CGSKeyboardEvent,
  Compatibility,
  Constructor,
  Container,
  SceneObject,
  SoundSceneObject,
  Stream,
  viewportChangeListener,
} from '@cgs/wge';
import { Key } from 'ts-keycode-enum';
import { SceneFactory, SCENE_FACTORY_TOKEN } from '@cgs/common';
import { ResourcesComponent } from '../../game/components/resources_component';
import { RESOURCES_COMPONENT_TOKEN } from '../../type_definitions';
import { PopupResizeFunction } from './popup.resize';
import { SoundInstance } from '../../reels_engine/sound_instance';

interface PopupSceneIdComplex {
  horizontal: string;
  vertical: string;
}

export interface PopupParams {
  sceneId: string | PopupSceneIdComplex;
  animSceneId?: string;
  isExistingScene?: boolean;
  showAnimState?: string;
  hideAnimState?: string;
  closeButtonId?: string | string[];
  closeSound?: string;
  closeBySpace?: boolean;
  drawOrder?: number | null;
  resize?: PopupResizeFunction;
}

const DEFAULT_PARAMS: Partial<PopupParams> = {
  drawOrder: 1100,
};

function isComplexScene(sceneId: string | PopupSceneIdComplex): sceneId is PopupSceneIdComplex {
  const complexSceneId = sceneId as PopupSceneIdComplex;
  return !!(complexSceneId.horizontal && complexSceneId.vertical);
}

export class Popup {
  public readonly showing: Stream<void> = new Stream();
  public readonly shown: Stream<void> = new Stream();
  public readonly closing: Stream<void> = new Stream();
  public readonly closed: Stream<void> = new Stream();

  private container: Container;
  private params: PopupParams;
  private rootScene: SceneObject;

  private popupScenes: SceneObject[] = [];
  private animScenes: SceneObject[] = [];
  private isShown: boolean = false;
  private subscriptions: (() => void)[] = [];
  private preexistingScenes: Set<SceneObject> = new Set();

  constructor(container: Container, params: PopupParams) {
    this.container = container;
    this.params = { ...DEFAULT_PARAMS, ...params };

    this.rootScene = container.resolve<ResourcesComponent>(RESOURCES_COMPONENT_TOKEN).root;
  }

  public get isOpen(): boolean {
    return this.isShown;
  }

  public show(): Promise<void> {
    if (this.isShown) {
      return Promise.resolve();
    }

    this.onShowing();

    this.eachPopupScene((scene) => {
      if (scene.parent) {
        this.preexistingScenes.add(scene);
      } else {
        this.rootScene.addChild(scene);
        scene.initialize();
      }
      if (this.params.drawOrder !== null) {
        scene.z = this.params.drawOrder!;
      }
      scene.active = true;
    });

    this.handleOrientationChange();
    this.handleClickClose();
    this.handleCloseBySpace();
    this.handleResize();

    this.isShown = true;

    if (this.params.showAnimState) {
      this.animScenes[0].stateMachine
        ?.findById(this.params.showAnimState)
        ?.enterAction.done.first.then(() => this.onShown());

      this.switchToState(this.params.showAnimState);

      return this.shown.toPromise();
    } else {
      this.onShown();
      return Promise.resolve();
    }
  }

  public hide(): Promise<void> {
    if (!this.isShown) {
      return Promise.resolve();
    }

    this.onClosing();

    const closePopup = () => {
      this.eachPopupScene((scene) => {
        scene.active = false;
        if (!this.preexistingScenes.has(scene)) {
          this.rootScene.removeChild(scene);
        }
      });

      requestAnimationFrame(() =>
        this.eachPopupScene((scene) => {
          if (!this.preexistingScenes.has(scene)) {
            scene.deinitialize();
          }
        })
      );

      this.subscriptions.forEach((unsubscribe) => unsubscribe());
      this.subscriptions = [];

      this.onClosed();
    };

    this.isShown = false;

    if (this.params.hideAnimState) {
      this.animScenes[0].stateMachine
        ?.findById(this.params.hideAnimState)
        ?.enterAction.done.first.then(() => closePopup());

      this.switchToState(this.params.hideAnimState);
      return this.closed.toPromise();
    } else {
      closePopup();
      return Promise.resolve();
    }
  }

  public switchToState(state: string): void {
    this.animScenes.forEach((scene) => scene.stateMachine?.switchToState(state));
  }

  public findById<T extends SceneObject>(id: string): T | null {
    this.checkAndRender();
    for (const scene of this.popupScenes) {
      const found = scene.findById<T>(id);
      if (found) {
        return found;
      }
    }
    return null;
  }

  public findAllById<T extends SceneObject>(id: string): T[] {
    this.checkAndRender();
    return this.popupScenes.flatMap((scene) => scene.findAllById<T>(id));
  }

  public findAllByType<T extends SceneObject>(type: Constructor<T>): T[] {
    this.checkAndRender();
    return this.popupScenes.flatMap((scene) => scene.findAllByType<T>(type));
  }

  protected onShowing(): void {
    this.showing.emit();
  }

  protected onShown(): void {
    this.shown.emit();
  }

  protected onClosing(): void {
    this.closing.emit();
  }

  protected onClosed(): void {
    this.closed.emit();
  }

  private get isRendered(): boolean {
    return this.popupScenes.length > 0;
  }

  private handleOrientationChange(): void {
    if (!isComplexScene(this.params.sceneId)) {
      return;
    }

    const orientationChange = () => {
      if (Compatibility.isPortrait) {
        this.popupScenes[0].visible = false;
        this.popupScenes[1].visible = true;
      } else {
        this.popupScenes[0].visible = true;
        this.popupScenes[1].visible = false;
      }
    };

    this.subscriptions.push(viewportChangeListener.on(() => orientationChange()));
    orientationChange();
  }

  private handleClickClose(): void {
    if (!this.params.closeButtonId) {
      return;
    }

    const closeButtonIds = Array.isArray(this.params.closeButtonId)
      ? this.params.closeButtonId
      : [this.params.closeButtonId];

    this.eachPopupScene((scene) => {
      closeButtonIds.forEach((buttonId) => {
        const button = scene.findById<Button>(buttonId);
        const subscription = button?.clicked.listen(() => {
          this.playCloseSound();
          this.hide();
        });
        if (subscription) {
          this.subscriptions.push(() => subscription.cancel());
        }
      });
    });
  }

  private handleCloseBySpace(): void {
    if (!this.params.closeBySpace) {
      return;
    }

    this.eachPopupScene((scene) => {
      const subscription = scene.eventReceived.listen((e) => {
        if (e instanceof CGSKeyboardEvent && e.keyCode === Key.Space) {
          e.accept();
          this.hide();
        }
      });
      this.subscriptions.push(() => subscription.cancel());
    });
  }

  private handleResize(): void {
    if (!this.params.resize) {
      return;
    }

    this.subscriptions.push(
      viewportChangeListener.on(() => {
        this.eachPopupScene((scene) => this.params.resize!(scene));
      })
    );

    this.eachPopupScene((scene) => this.params.resize!(scene));
  }

  private renderPopup(): void {
    const sceneFactory = this.container.resolve<SceneFactory>(SCENE_FACTORY_TOKEN);

    if (!isComplexScene(this.params.sceneId)) {
      let scene: SceneObject | null;

      if (this.params.isExistingScene) {
        scene = this.rootScene.findById(this.params.sceneId);
      } else {
        scene = sceneFactory.build(this.params.sceneId);
      }

      if (!scene) {
        throw new Error(`${Popup.name}: cannot find scene with id ${this.params.sceneId}`);
      }

      this.popupScenes = [scene];
    } else {
      let scenes: (SceneObject | null)[];

      if (this.params.isExistingScene) {
        scenes = [
          this.rootScene.findById(this.params.sceneId.horizontal),
          this.rootScene.findById(this.params.sceneId.vertical),
        ];
      } else {
        scenes = [
          sceneFactory.build(this.params.sceneId.horizontal),
          sceneFactory.build(this.params.sceneId.vertical),
        ];
      }

      const index = scenes.findIndex((scene) => !scene);
      if (index !== -1) {
        throw new Error(
          `${Popup.name}: cannot find scene with id ${index === 0 ? this.params.sceneId.horizontal : this.params.sceneId.vertical}`
        );
      }

      this.popupScenes = scenes as SceneObject[];
    }

    this.animScenes = this.popupScenes.map((scene) => {
      if (!this.params.animSceneId) {
        return scene;
      }

      const animScene = scene.findById(this.params.animSceneId);

      if (!animScene) {
        throw new Error(`${Popup.name}: cannot find anim scene with id ${this.params.animSceneId}`);
      }

      return animScene;
    });
  }

  public eachPopupScene(callback: (scene: SceneObject) => void): void {
    this.checkAndRender();
    this.popupScenes.forEach(callback);
  }

  private playCloseSound(): void {
    if (!this.params.closeSound) {
      return;
    }
    const resourcesComponent =
      this.container.resolve<ResourcesComponent>(RESOURCES_COMPONENT_TOKEN);
    const closeSoundScene = resourcesComponent.sounds
      .findById<SoundSceneObject>(this.params.closeSound)
      ?.findAllByType(SoundSceneObject)[0];
    const closeSound = new SoundInstance(closeSoundScene || null);
    closeSound.stop();
    closeSound.play();
  }

  private checkAndRender(): void {
    if (!this.isRendered) {
      this.renderPopup();
    }
  }
}
